import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { ThemeStateService } from '@sites/site-theme/src/state';
import type { PagesButtonsFormGroup } from '@web/widgets/catalog-products/type';
import { type PagesButtonsFormType } from '@web/widgets/catalog-products/type';
import { ELineStyle, type ESize } from '@common/enums';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { CSS_RESTRICTIONS } from '@common/constants';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { CommonModule } from '@angular/common';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

@Component({
    selector: 'web-pages-settings',
    templateUrl: './pages-settings.component.html',
    styleUrls: ['./pages-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, CommonElementsModule],
})
export class PagesSettingsComponent {
    @Input() pagesButtonsFormGroup: PagesButtonsFormGroup;

    private readonly themeState = inject(ThemeStateService);

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public get textColor(): PagesButtonsFormType['textColor'] {
        return this.pagesButtonsFormGroup.get('textColor') as PagesButtonsFormType['textColor'];
    }

    public get backgroundColor(): PagesButtonsFormType['backgroundColor'] {
        return this.pagesButtonsFormGroup.get('backgroundColor') as PagesButtonsFormType['backgroundColor'];
    }

    public get size(): PagesButtonsFormType['inputSize'] {
        return this.pagesButtonsFormGroup.get('inputSize') as PagesButtonsFormType['inputSize'];
    }

    public get borderRadius(): PagesButtonsFormType['borderRadius'] {
        return this.pagesButtonsFormGroup.get('borderRadius') as PagesButtonsFormType['borderRadius'];
    }

    public get borderColor(): PagesButtonsFormType['borderColor'] {
        return this.pagesButtonsFormGroup.get('borderColor') as PagesButtonsFormType['borderColor'];
    }

    public setSize(size: string): void {
        this.size.patchValue(size as ESize);
    }

    public setBorderRadius(value: number): void {
        this.pagesButtonsFormGroup.markAsTouched({ onlySelf: true });

        this.borderRadius.patchValue(value);
    }

    public setThemeBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.pagesButtonsFormGroup.markAsTouched({ onlySelf: true });

        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.pagesButtonsFormGroup.markAsTouched({ onlySelf: true });

        this.borderColor.patchValue(presetName ?? value);
    }

    public setTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.pagesButtonsFormGroup.markAsTouched({ onlySelf: true });

        this.textColor.patchValue(presetName ?? value);
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    protected readonly lineStyle = ELineStyle;
    protected readonly ERadioGroupType = ERadioGroupType;
}
